import { useEffect, useState } from 'react';
import { Layout, Spin, Alert, Typography, Button } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getElectronicTicketList, getElectronicTicketStats } from '../api/store';
import dayjs from 'dayjs';
import '../utils/dayjs-config';
import './ElectronicTicket.css';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

const headerStyle = {
  textAlign: 'center',
  color: '#fff',
  height: 64,
  paddingInline: 48,
  lineHeight: '64px',
  backgroundColor: '#ED4948',
};

const ElectronicTicket = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const storeId = searchParams.get('storeId');
  const storeName = searchParams.get('storeName');
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({
    turnover: 0,
    orderNum: 0,
  });
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    if (!storeId || !startDate || !endDate) {
      setError('缺少必要的查询参数，无法加载电子票数据');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const listResponse = await getElectronicTicketList(storeId, startDate, endDate);

        // 处理返回数据结构
        const responseData = listResponse?.data || listResponse || {};
        
        // 更新汇总信息
        setSummary({
          turnover: responseData.total_amount || 0,
          orderNum: responseData.total_orders || 0,
        });

        // 处理电子票列表数据
        const ticketList = responseData.list || [];
        const normalizedItems = ticketList.map((item, index) => ({
          key: item.num_serie || `ticket-${index}`,
          num_serie: item.num_serie || '--',
          fecha_expedicion: item.fecha_expedicion || '--',
          importe_total: item.importe_total || 0,
          nombre_destinatario: item.nombre_destinatario || '',
        }));
        setTickets(normalizedItems);
      } catch (err) {
        console.error('加载电子票数据失败:', err);
        setError(err?.response?.data?.message || err.message || '加载电子票数据失败');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [storeId, startDate, endDate]);

  const handleBack = () => navigate(-1);

  // 计算时间段长度（天数）
  const getDateDiff = (start, end) => {
    return dayjs(end).diff(dayjs(start), 'day') + 1;
  };

  // 切换到上一个时间段
  const handlePrevPeriod = () => {
    const daysDiff = getDateDiff(startDate, endDate);
    const newStartDate = dayjs(startDate).subtract(daysDiff, 'day').format('YYYY-MM-DD');
    const newEndDate = dayjs(endDate).subtract(daysDiff, 'day').format('YYYY-MM-DD');
    
    // 更新URL参数
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set('startDate', newStartDate);
    newParams.set('endDate', newEndDate);
    navigate(`/electronic-ticket?${newParams.toString()}`, { replace: true });
  };

  // 切换到下一个时间段
  const handleNextPeriod = () => {
    const daysDiff = getDateDiff(startDate, endDate);
    const newStartDate = dayjs(startDate).add(daysDiff, 'day').format('YYYY-MM-DD');
    const newEndDate = dayjs(endDate).add(daysDiff, 'day').format('YYYY-MM-DD');
    
    // 检查是否超过今天
    const today = dayjs().format('YYYY-MM-DD');
    if (dayjs(newStartDate).isAfter(today)) {
      return; // 不能选择未来的日期
    }
    
    // 更新URL参数
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set('startDate', newStartDate);
    newParams.set('endDate', newEndDate);
    navigate(`/electronic-ticket?${newParams.toString()}`, { replace: true });
  };

  return (
    <Layout className="electronic-ticket">
      <Header style={headerStyle}>
        <div className="header-container">
          <div className="header-button" />
          <div>{storeName ? `${storeName}电子票` : '电子票'}</div>
          <div className="header-button" onClick={handleBack}>返回</div>
        </div>
      </Header>
      <Content className="ticket-content">
        <div className="summary-section">
          <div className="summary-card date-range-top">
            <Text className="summary-label">时间范围</Text>
            <div className="date-navigation">
              <Button 
                type="text" 
                icon={<LeftOutlined />} 
                onClick={handlePrevPeriod}
                className="date-nav-btn"
              />
              <Text className="summary-value">{startDate} ~ {endDate}</Text>
              <Button 
                type="text" 
                icon={<RightOutlined />} 
                onClick={handleNextPeriod}
                className="date-nav-btn"
              />
            </div>
          </div>
          <div className="summary-row">
            <div className="summary-card">
              <Text className="summary-label">总收入</Text>
              <Title level={3} className="summary-value">{summary.turnover}</Title>
            </div>
            <div className="summary-card">
              <Text className="summary-label">总单数</Text>
              <Title level={3} className="summary-value">{summary.orderNum}</Title>
            </div>
          </div>
        </div>

        {error && (
          <Alert
            className="ticket-alert"
            message={error}
            type="error"
            showIcon
          />
        )}

        <div className="table-card">
          <Spin spinning={loading} tip="加载中...">
            {tickets.length > 0 ? (
              <div className="ticket-list">
                {tickets.map((ticket) => (
                  <div key={ticket.key} className="ticket-item">
                    <div className="ticket-row-1">
                      <div className="ticket-field">
                        <span className="field-label">电子票号:</span>
                        <span className="field-value">{ticket.num_serie}</span>
                      </div>
                      <div className="ticket-field">
                        <span className="field-label">总金额:</span>
                        <span className="field-value">{ticket.importe_total}</span>
                      </div>
                    </div>
                    <div className="ticket-row-2">
                      <div className="ticket-field">
                        <span className="field-label">开票日期:</span>
                        <span className="field-value">{ticket.fecha_expedicion}</span>
                      </div>
                      <div className="ticket-field">
                        <span className="field-label">客户名称:</span>
                        <span className="field-value">{ticket.nombre_destinatario || '--'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                {error ? '暂无数据' : '暂无电子票记录'}
              </div>
            )}
          </Spin>
        </div>
      </Content>
    </Layout>
  );
};

export default ElectronicTicket;
